import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { delay, filter, switchMap, take, tap } from 'rxjs/operators';

import {
  AddonType,
  BreakPointsMaxInPx,
  CookiePreference,
  CustomerSettings,
  LandlordUser,
  UserPermission
} from '@ui/shared/models';
import {
  CookieBannerComponent,
  CountUnread,
  ElevationDirective,
  fadeOnEnterLeaveAnimation,
  FooterComponent,
  getCookiesPreference,
  getEmailVerifiedInCurrentSession,
  GetPerformanceWarningConfirmation,
  getPerformanceWarningConfirmed,
  getRouterUrl,
  HeaderComponent,
  LocalStorageService,
  MessengerNotificationService,
  NavigationComponent,
  NavigationUserProfileComponent,
  OpenCookieSettingsModal,
  PerformanceWarningBannerComponent,
  PermissionService,
  POLLING_TIME_LL,
  SessionStorageService,
  SetCookiesPreference,
  SetPerformanceWarningConfirmation,
  slideAnimation,
  SlideAnimationStateEnum,
  SurvicateService,
  VerifyEmailBannerComponent
} from '@ui/legacy-lib';

import {
  authorizedFooterConfig,
  navigationConfig,
  productBoardUrlPlaceholder,
  profileSettingsNavigationConfig
} from 'landlord/config';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ProspectSearchComponent } from 'landlord/search/prospect-search/prospect-search.component';
import { LandlordNavigationItem } from 'landlord/models/landlord-navigation-item.model';
import { SharedUserStore } from '@ui/landlord/shared/state/user';
import { toObservable } from '@angular/core/rxjs-interop';
import { storageKeys } from '@ui/landlord/shared/config';
import { ChatWidgetService } from 'landlord/main';
import * as fromAccountState from '../../screens/account/+state';
import { getProductOverviewActionState } from '../../screens/account/+state';

interface NavigationStoredSettings {
  narrow?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-fullscreen-authenticated',
  templateUrl: './fullscreen-authenticated.component.html',
  styleUrls: ['./fullscreen-authenticated.component.scss'],
  animations: [fadeOnEnterLeaveAnimation, slideAnimation],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CookieBannerComponent,
    HeaderComponent,
    NgTemplateOutlet,
    NavigationComponent,
    PerformanceWarningBannerComponent,
    VerifyEmailBannerComponent,
    RouterOutlet,
    NgClass,
    NavigationUserProfileComponent,
    FooterComponent,
    ElevationDirective,
    AsyncPipe,
    ProspectSearchComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FullscreenAuthenticatedComponent
  implements OnInit, OnDestroy, OnChanges
{
  #chatWidgetService = inject(ChatWidgetService, { optional: true });
  private permissionService = inject(PermissionService);
  private store = inject(Store);
  private sessionStorage = inject(SessionStorageService);
  private observer = inject(BreakpointObserver);
  private messengerNotificationService = inject(MessengerNotificationService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private storageService = inject(LocalStorageService);
  private sharedUserStore = inject(SharedUserStore);

  public navigationOpen: boolean;
  public navigationStoredSettings: NavigationStoredSettings;
  public profileSettingsOpen: boolean;

  // This sets the profile settings navigation to display: none or block
  // It's required to not render this space, because in the app-tab-navigation component it will be scrolled in to the view.
  public showProfileSettingsNavigationInTemplate = false;
  private _mobileView: boolean;

  public navigationItems: LandlordNavigationItem[];
  private _currentNavigationItem: LandlordNavigationItem;

  public profileSettingsItems: LandlordNavigationItem[] =
    profileSettingsNavigationConfig;
  public footerLinks: typeof authorizedFooterConfig.links;

  public slideAnimationStateEnum = SlideAnimationStateEnum;
  public addonsType: AddonType[];
  public cookiesPreference$: Observable<CookiePreference>;
  public confirmedPerformanceWarning$: Observable<boolean>;
  public hasEmailVerified$: Observable<boolean>;
  public verificationEmailResent$: Observable<boolean>;
  public emailVerifiedInCurrentSession$: Observable<boolean>;
  public hideEmailBanner$: Observable<boolean>;

  public userData: LandlordUser;
  private customerSettings$ = toObservable(
    this.sharedUserStore.customerSettings
  );

  public showWarningBanner = false;
  public warningBannerConfig = {
    header: 'landlord.performance_banner.header_l',
    message: 'landlord.performance_banner.message_l'
  };
  #survicateService = inject(SurvicateService);

  public displayBannerAndFloatingButtons = true;

  constructor() {
    this.router.events
      .pipe(
        filter(event => !!(event instanceof NavigationEnd)),
        untilDestroyed(this)
      )
      .subscribe((event: NavigationEnd) => {
        return (this._currentNavigationItem = this.get_currentNavigationItem(
          event.url
        ));
      });
  }

  public ngOnInit() {
    this.store.dispatch(new CountUnread(POLLING_TIME_LL));
    this.#chatWidgetService?.initWidget?.();
    this.navigationStoredSettings = this.storageService.getItem(
      storageKeys.navigation
    );

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        filter(
          (event: NavigationStart) =>
            event.url.includes('logout') && event.url.includes('side:aux')
        ),
        untilDestroyed(this)
      )
      .subscribe(() => {
        void this.router.navigate(['', { outlets: { side: null } }]);
        setTimeout(() => {
          void this.router.navigate(['/logout']);
        });
      });

    this.store
      .select(fromAccountState.getProductBoardUrl)
      .pipe(untilDestroyed(this))
      .subscribe(url => {
        this.setFooterLinks(
          authorizedFooterConfig.links.map(link => {
            return {
              ...link,
              url: link?.url.replace(productBoardUrlPlaceholder, url)
            };
          })
        );
      });
    this.store.dispatch(new fromAccountState.GetProductBoardUrl());

    this.observer
      .observe(`(max-width: ${BreakPointsMaxInPx.MD}px)`)
      .pipe(untilDestroyed(this))
      .subscribe(result => (this._mobileView = result.matches));

    this.showWarningBanner =
      this.translate.instant('SHOW_PERFORMANCE_BANNER_LL') === 'ENABLED';

    if (
      this.sessionStorage.getItem(storageKeys.emailVerifiedInCurrentSession)
    ) {
      this.sessionStorage.removeItem(storageKeys.emailVerifiedInCurrentSession);
    }

    this.store.dispatch(new GetPerformanceWarningConfirmation());

    this.cookiesPreference$ = this.store.select(getCookiesPreference);
    this.confirmedPerformanceWarning$ = this.store.select(
      getPerformanceWarningConfirmed
    );

    this.emailVerifiedInCurrentSession$ = this.store.select(
      getEmailVerifiedInCurrentSession
    );
    this.hasEmailVerified$ = this.store.select(
      fromAccountState.getHasEmailVerified
    );
    this.verificationEmailResent$ = this.store.select(
      fromAccountState.getVerificationEmailResent
    );
    this.hideEmailBanner$ = this.store
      .select(getEmailVerifiedInCurrentSession)
      .pipe(delay(5000), take(1));

    this.store
      .select(getRouterUrl)
      .pipe(untilDestroyed(this))
      .subscribe(url => {
        if (url.includes('side:aux') || url.includes('messenger')) {
          this.displayBannerAndFloatingButtons = false;
        } else {
          this.displayBannerAndFloatingButtons = true;
        }
      });

    this.messengerNotificationService.init();

    this.messengerNotificationService.unreadCount$
      .pipe(untilDestroyed(this))
      .subscribe(unreadMessagesCount => {
        this.injectUnreadMessagesCountToNavigationItems(unreadMessagesCount);
      });

    this.store
      .select(fromAccountState.getUserData)
      .pipe(
        tap(userData => {
          this.userData = userData;
          void this.#survicateService.init(userData.email);
        }),
        switchMap(userData =>
          this.store.select(getProductOverviewActionState).pipe(
            filter(state => state.done),
            switchMap(() =>
              combineLatest([
                this.messengerNotificationService.unreadCount$,
                this.customerSettings$
              ]).pipe(
                tap(([unreadMessagesCount, customerSettings]) => {
                  this.setNavigationItems(
                    this.getAccessibleNavItems(
                      navigationConfig,
                      this.permissionService.getPermissions(),
                      userData?.bookedAddons,
                      customerSettings
                    )
                  );
                  this.injectUnreadMessagesCountToNavigationItems(
                    unreadMessagesCount
                  );
                })
              )
            )
          )
        ),

        untilDestroyed(this)
      )
      .subscribe();
  }

  public ngOnChanges() {
    this._currentNavigationItem = this.get_currentNavigationItem(
      this.router.routerState.snapshot.url
    );
  }

  ngOnDestroy() {
    this.messengerNotificationService.complete();
  }

  private get_currentNavigationItem(currentUrl: string) {
    return this.navigationItems?.find(
      item => currentUrl.indexOf(item.link) > -1
    );
  }

  public acceptCookies(payload: CookiePreference) {
    this.store.dispatch(new SetCookiesPreference(payload));
  }

  public customCookieSettings(payload: CookiePreference) {
    this.store.dispatch(new OpenCookieSettingsModal(payload, false));
  }

  public confirmPerformanceWarning() {
    this.store.dispatch(new SetPerformanceWarningConfirmation(true));
  }

  public resendVerificationEmail() {
    this.store.dispatch(new fromAccountState.ResendVerificationEmail());
  }

  private injectUnreadMessagesCountToNavigationItems(
    unreadMessagesCount: number
  ): void {
    if (this.navigationItems) {
      this.setNavigationItems(
        this.navigationItems.map(item =>
          item.name === 'messenger'
            ? {
                ...item,
                notificationCount: unreadMessagesCount
              }
            : item
        )
      );
    }
  }

  private setNavigationItems(items: LandlordNavigationItem[]) {
    this.navigationItems = items;
  }

  private setFooterLinks(links: typeof authorizedFooterConfig.links) {
    this.footerLinks = links;
  }

  public onNavigationToggleClick(event: Event) {
    event.stopPropagation();

    this.navigationOpen = !this.navigationOpen;

    if (this.navigationOpen) this.profileSettingsOpen = false;
  }

  public onNavigationNarrowManualChange(narrow: boolean) {
    this.storageService.setItem(storageKeys.navigation, {
      ...this.navigationStoredSettings,
      narrow
    });
  }

  public onProfileSettingsToggleClick(event: Event) {
    event.stopPropagation();

    if (!this.profileSettingsOpen)
      this.showProfileSettingsNavigationInTemplate = true;

    this.profileSettingsOpen = !this.profileSettingsOpen;

    if (this._mobileView && this.profileSettingsOpen) {
      this.navigationOpen = false;
    }
  }

  private hasPermissions(
    userPermissions: ReadonlyArray<string>,
    withPermission: string[],
    requirementType: 'any' | 'all' = 'any'
  ): boolean {
    if (requirementType === 'all') {
      return withPermission.every(permission =>
        userPermissions.includes(permission)
      );
    }
    return withPermission.some(permission =>
      userPermissions.includes(permission)
    );
  }

  /**
   * The navigation items can also be hidden based on various functions
   * Hopefully this can be refactored and trashed in the future
   * @param navItem
   * @param bookedAddons
   * @param customerSettings
   */
  hideNavItemBasedOnMisc(
    navItem: LandlordNavigationItem,
    bookedAddons: AddonType[],
    customerSettings: CustomerSettings
  ) {
    if (
      Object.hasOwn(navItem, 'hideOnSingleAddonFn') &&
      navItem.hideOnSingleAddonFn(bookedAddons)
    ) {
      return true;
    }

    if (
      Object.hasOwn(navItem, 'hideOnCustomerSettings') &&
      navItem.hideOnCustomerSettings(customerSettings)
    ) {
      return true;
    }

    return false;
  }

  /**
   * Also checks child navigation items recursively
   * @param navItems
   * @param userPermissions
   * @param bookedAddons
   * @param customerSettings
   * @private
   */
  private getAccessibleNavItems(
    navItems: LandlordNavigationItem[],
    userPermissions: ReadonlyArray<string>,
    bookedAddons: AddonType[],
    customerSettings: CustomerSettings
  ): LandlordNavigationItem[] {
    return (
      navItems
        .map(item => {
          if (
            // Only call this.hasPermissions if the withPermission array exists
            Object.hasOwn(item, 'withPermission') &&
            !this.hasPermissions(
              userPermissions,
              item.withPermission,
              item.requirementType
            )
          ) {
            return null; // Skip inaccessible items
          }

          if (
            // Only call this.hasPermissions if the withAddon array exists
            Object.hasOwn(item, 'withAddon') &&
            !this.hasPermissions(
              bookedAddons,
              item.withAddon,
              item.requirementType
            )
          ) {
            return null; // Skip inaccessible items
          }

          if (
            this.hideNavItemBasedOnMisc(item, bookedAddons, customerSettings)
          ) {
            return null; // Skip hidden items
          }

          let accessibleChildren = [];
          if (item.children) {
            // Recursively process children if present
            accessibleChildren = this.getAccessibleNavItems(
              item.children,
              userPermissions,
              bookedAddons,
              customerSettings
            );
          }

          return {
            ...item,
            children:
              // Include children only if they have at least one visible item
              accessibleChildren.length > 0 ? accessibleChildren : undefined
          };
        })
        // Remove null values (inaccessible items)
        .filter(Boolean)
    );
  }

  protected readonly UserPermission = UserPermission;
}
